import { useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Loader2, AlertCircle, ChevronLeft, Calendar, Info } from 'lucide-react'
import { SnapshotDashboard } from '@/components/attendance-metrics/SnapshotDashboard'
import { SnapshotExportButton } from '@/components/attendance-metrics/SnapshotExportButton'
import { CompareSnapshotDropdown } from '@/components/attendance-metrics/CompareSnapshotDropdown'
import { useSnapshot } from '@/hooks/useAttendanceMetrics'
import { ROUTES } from '@/lib/constants'
import { Alert, AlertDescription } from '@/components/ui/alert'

export function SnapshotDashboardPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [compareSnapshotId, setCompareSnapshotId] = useState<string | null>(null)

  const handleBack = () => {
    if (location.key !== 'default') {
      navigate(-1)
    } else {
      navigate(ROUTES.ATTENDANCE_METRICS_SNAPSHOTS)
    }
  }

  // Fetch snapshot data
  const snapshotQuery = useSnapshot(id!, { enabled: !!id })
  const compareQuery = useSnapshot(compareSnapshotId!, {
    enabled: !!compareSnapshotId,
  })

  // Loading state
  if (snapshotQuery.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Carregando relatório...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (snapshotQuery.error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4 max-w-md text-center">
          <AlertCircle className="h-12 w-12 text-destructive" />
          <div>
            <h3 className="text-lg font-semibold mb-2">Erro ao carregar relatório</h3>
            <p className="text-sm text-muted-foreground">
              {snapshotQuery.error instanceof Error
                ? snapshotQuery.error.message
                : 'Ocorreu um erro inesperado'}
            </p>
          </div>
          <Button onClick={handleBack}>
            Voltar para lista
          </Button>
        </div>
      </div>
    )
  }

  const snapshot = snapshotQuery.data

  if (!snapshot) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4 max-w-md text-center">
          <AlertCircle className="h-12 w-12 text-destructive" />
          <div>
            <h3 className="text-lg font-semibold mb-2">Relatório não encontrado</h3>
            <p className="text-sm text-muted-foreground">
              O relatório solicitado não existe ou foi removido
            </p>
          </div>
          <Button onClick={handleBack}>
            Voltar para lista
          </Button>
        </div>
      </div>
    )
  }

  // Check if dashboard data exists
  if (!snapshot.dashboardData) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBack}
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold">{snapshot.name}</h1>
              <p className="text-muted-foreground">
                {new Date(snapshot.startDate).toLocaleDateString('pt-BR')} até{' '}
                {new Date(snapshot.endDate).toLocaleDateString('pt-BR')}
              </p>
            </div>
          </div>
        </div>

        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Este relatório não possui dados de dashboard. Ele pode ter sido criado antes da
            funcionalidade ser implementada.
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBack}
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold">{snapshot.name}</h1>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="h-4 w-4" />
              <span>
                {new Date(snapshot.startDate).toLocaleDateString('pt-BR')} até{' '}
                {new Date(snapshot.endDate).toLocaleDateString('pt-BR')}
              </span>
              <span className="text-muted-foreground/50">•</span>
              <span>Criado em {new Date(snapshot.createdAt).toLocaleDateString('pt-BR')}</span>
            </div>
          </div>
          <SnapshotExportButton snapshotId={snapshot.id} />
        </div>

        {/* Comparison Controls */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Comparação</CardTitle>
          </CardHeader>
          <CardContent>
            <CompareSnapshotDropdown
              currentSnapshotId={snapshot.id}
              selectedSnapshotId={compareSnapshotId}
              onSnapshotSelect={setCompareSnapshotId}
            />
            {compareSnapshotId && compareQuery.isLoading && (
              <div className="flex items-center gap-2 mt-4 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Carregando dados de comparação...</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dashboard */}
      <SnapshotDashboard
        snapshotData={snapshot.dashboardData}
        comparisonData={compareQuery.data?.dashboardData}
      />
    </div>
  )
}
